import React from 'react';
import PropTypes from 'prop-types';
import { Callout, DirectionalHint } from '@fluentui/react';
import { mapObject, constant, bind } from 'underscore';
import { Row } from '@bingads-webui-react/layout';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { getAdTypeItemStyles } from '../styles';
import { StyledSelectableItem } from './selectable-item';

class AdTypeItem extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isSmartShoppingWarningVisible: false,
      isPerfMaxWarningVisible: false,
    };
    this.warningMessageTarget = React.createRef();
  }

  handleAdTypeClick = () => {
    const {
      disabled, onSelect, id, itemId, dealDetails,
    } = this.props;

    if (disabled) {
      return;
    }

    if (itemId === 'MediaPackageCampaign') {
      onSelect(dealDetails);

      return;
    }
    onSelect(id);
  };

  showWarning(isShowWarning) {
    if (isShowWarning && this.props.isSmartShoppingWarningVisible) {
      this.setState({
        isSmartShoppingWarningVisible: true,
      });
    }
    if (isShowWarning && this.props.isPerfMaxWarningVisible) {
      this.setState({
        isPerfMaxWarningVisible: true,
      });
    }
    if (!isShowWarning && this.state.isSmartShoppingWarningVisible) {
      this.setState({
        isSmartShoppingWarningVisible: false,
      });
    }
    if (!isShowWarning && this.state.isPerfMaxWarningVisible) {
      this.setState({
        isPerfMaxWarningVisible: false,
      });
    }
  }

  render() {
    const {
      smartShoppingWarningMessage, disabled, classes, title, description, img, id, isSelected, audienceAdsPolicy, period, 'aria-label': ariaLabel, ariaItemSize, ariaItemIndex, i18n, isNewFeature, helpTopicId,
    } = this.props;

    return (
      <div ref={this.warningMessageTarget} className={`${classes.adTypeItem} ad-type-item ${disabled ? 'disabled' : ''}`}>
        <StyledSelectableItem
          id={id}
          onClick={this.handleAdTypeClick}
          isSelected={isSelected}
          aria-label={ariaLabel}
          ariaItemSize={ariaItemSize}
          ariaItemIndex={ariaItemIndex}
          showWarning={bind(this.showWarning, this)}
        >
          {
            isNewFeature ?
              <div className={`${classes.newFeatureLabel} new-feature-label`}>{i18n.getString(_TL_('New'))}</div>
              : ''
          }
          <Row className="title-row">
            <span className={`${classes.title} ad-type-title`}>{title}</span>
          </Row>
          {
            img &&
            <Row className="img-row">
              <img src={img} className={`${classes.img} ad-type-img`} alt={title} />
            </Row>
          }
          <Row className="description-row">
            <span className={`${classes.description} ad-type-description`}>
              {description}
              {
                audienceAdsPolicy ?
                  <a target="_blank" rel="noopener noreferrer" href="https://go.microsoft.com/fwlink/?linkid=2028852">{audienceAdsPolicy}</a>
                  : ''
              }
              <span>{period}</span>
              {
                helpTopicId ?
                  <LearnMoreLink
                    linkText={i18n.getString(_TL_('Learn more'))}
                    ariaLabel={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: title })}
                    topic={helpTopicId}
                  />
                : null
              }
            </span>
          </Row>
        </StyledSelectableItem>
        {this.state.isSmartShoppingWarningVisible && smartShoppingWarningMessage ? (
          <Callout
            className={classes.smartShoppingWarningCallout}
            target={this.warningMessageTarget.current}
            ariaLabel={_TL_('Smart shopping campaign not supported warning')}
            role="alertdialog"
            directionalHint={DirectionalHint.leftCenter}
            calloutWidth={309}
          >
            {smartShoppingWarningMessage}
          </Callout>
        ) : null}
        {this.state.isPerfMaxWarningVisible && smartShoppingWarningMessage ? (
          <Callout
            className={classes.smartShoppingWarningCallout}
            target={this.warningMessageTarget.current}
            ariaLabel={_TL_('Perf max campaign not supported warning')}
            role="alertdialog"
            directionalHint={DirectionalHint.rightCenter}
            calloutWidth={309}
          >
            {smartShoppingWarningMessage}
          </Callout>
        ) : null}
      </div>
    );
  }
}

AdTypeItem.propTypes = {
  isSmartShoppingWarningVisible: PropTypes.bool,
  isPerfMaxWarningVisible: PropTypes.bool,
  smartShoppingWarningMessage: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  classes: PropTypes.shape(mapObject(
    getAdTypeItemStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  img: PropTypes.string.isRequired,
  helpTopicId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  audienceAdsPolicy: PropTypes.string,
  period: PropTypes.string,
  'aria-label': PropTypes.string,
  ariaItemSize: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  ariaItemIndex: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isNewFeature: PropTypes.bool,
  itemId: PropTypes.string,
  dealDetails: PropTypes.shape({}),
};

AdTypeItem.defaultProps = {
  isPerfMaxWarningVisible: false,
  isSmartShoppingWarningVisible: false,
  smartShoppingWarningMessage: '',
  audienceAdsPolicy: null,
  period: null,
  dealDetails: null,
  helpTopicId: '',
  itemId: '',
  disabled: false,
  'aria-label': '',
  isNewFeature: false,
};

export const StyledAdTypeItem = withDefaultStyles(AdTypeItem, getAdTypeItemStyles);
